/**
 * Scheduled-job wall clocks are stored in the job's IANA timezone (often UTC).
 * The edit modal shows and edits them in Asia/Dhaka (UTC+6).
 */
export const SCHEDULE_DISPLAY_TIME_ZONE = 'Asia/Dhaka';

const ISO_TIMEZONE_SUFFIX = /(?:z|[+-]\d{2}:\d{2})$/i;

const pad2 = (n: number) => String(n).padStart(2, '0');

type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function timeZoneParts(date: Date, timeZone: string): ZonedParts {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const map: Record<string, string> = {};
  for (const part of dtf.formatToParts(date)) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

function ymdKey(parts: Pick<ZonedParts, 'year' | 'month' | 'day'>): string {
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

function addCalendarDays(parts: Pick<ZonedParts, 'year' | 'month' | 'day'>, days: number) {
  const utc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  };
}

function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = timeZoneParts(date, timeZone);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return asUtc - date.getTime();
}

export function wallClockToUtcDate(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string
): Date {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const offset = getTimeZoneOffsetMs(new Date(utcGuess), timeZone);
  return new Date(utcGuess - offset);
}

export function parseIsoInstant(value?: string | null): Date | null {
  if (!value) return null;
  const normalized = ISO_TIMEZONE_SUFFIX.test(value) ? value : `${value}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Prefer the UTC instant (`nextRunAt`). Naive `nextRunAtLocal` is often a UTC
 * wall clock without a suffix — treating it as the browser's local time shows
 * UTC hours in UTC+6. Offset-aware `nextRunAtLocal` is a real instant.
 */
export function resolveScheduleNextRunDate(
  nextRunAtLocal?: string | null,
  nextRunAt?: string | null
): Date | null {
  const utc = parseIsoInstant(nextRunAt);
  if (utc) return utc;

  if (nextRunAtLocal) {
    if (ISO_TIMEZONE_SUFFIX.test(nextRunAtLocal)) {
      const instant = new Date(nextRunAtLocal);
      if (!Number.isNaN(instant.getTime())) return instant;
    }
    return parseIsoInstant(nextRunAtLocal);
  }

  return null;
}

export function formatTimeOfDayInTimeZone(date: Date, timeZone: string): string {
  const parts = timeZoneParts(date, timeZone);
  return `${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

export function convertTimeOfDay(
  hhmm: string,
  fromTimeZone: string,
  toTimeZone: string,
  reference = new Date()
): string {
  if (!hhmm || fromTimeZone === toTimeZone) return hhmm;
  const parsed = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!parsed) return hhmm;
  const hour = parseInt(parsed[1], 10);
  const minute = parseInt(parsed[2], 10);
  const refParts = timeZoneParts(reference, fromTimeZone);
  const instant = wallClockToUtcDate(refParts.year, refParts.month, refParts.day, hour, minute, fromTimeZone);
  return formatTimeOfDayInTimeZone(instant, toTimeZone);
}

/** `datetime-local` value (`YYYY-MM-DDTHH:mm`) in `timeZone`. */
export function isoToDatetimeLocal(iso: string, timeZone: string): string {
  const date = parseIsoInstant(iso);
  if (!date) return '';
  const parts = timeZoneParts(date, timeZone);
  return `${ymdKey(parts)}T${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

export function isoToDateInput(iso: string, timeZone: string): string {
  return isoToDatetimeLocal(iso, timeZone).slice(0, 10);
}

export function datetimeLocalToIso(value: string, timeZone: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(trimmed);
  if (!match) return trimmed;
  return wallClockToUtcDate(
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    timeZone
  ).toISOString();
}

export function endOfLocalDateIso(dateValue: string, timeZone: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue.trim());
  if (!match) return undefined;
  const instant = wallClockToUtcDate(
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    23,
    59,
    timeZone
  );
  instant.setUTCSeconds(59, 0);
  return instant.toISOString();
}

export function formatInstantLabel(
  date: Date,
  timeZone: string = SCHEDULE_DISPLAY_TIME_ZONE
): string {
  const nowParts = timeZoneParts(new Date(), timeZone);
  const dateParts = timeZoneParts(date, timeZone);
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
  const dateKey = ymdKey(dateParts);
  if (dateKey === ymdKey(nowParts)) return `Today at ${time}`;
  if (dateKey === ymdKey(addCalendarDays(nowParts, 1))) return `Tomorrow at ${time}`;
  const medium = new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
  return medium.replace(', ', ' at ');
}

export function formatRunTimestamp(
  value?: string | null,
  timeZone: string = SCHEDULE_DISPLAY_TIME_ZONE
): string {
  const date = parseIsoInstant(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}
